import { useState } from "react";
import axios, { isAxiosError } from "axios";
import { Alert, Center, Loader, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { IconCircleCheckFilled, IconUpload } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { w9RequestsEndpoints } from "@/api/w9-requests/w9Requests.endpoints";
import { w9PublicAxios } from "@/api/w9-requests/w9Requests.publicClient";
import { Button } from "@/components/ui/Button/Button";
import { FileUploader } from "@/components/ui/FileUploader/FileUploader";
import { getApiErrorMessage } from "@/utils/responseHandler";

import type {
  W9PublicLinkInfo,
  W9PublicLinkInfoResponse,
  W9PublicUploadUrlResponse,
} from "@/api/w9-requests/w9Requests.types";
import type { ApiErrorResponse } from "@/types/common.types";
import type { FileDescriptor } from "@/components/ui/FileUploader/FileUploader.types";

// Public, token-gated page — providers open it from the SMS link with no
// session, so it deliberately has no auth guard and uses the bare w9PublicAxios
// client (no auth interceptors / 401 redirects).
export const Route = createFileRoute("/sp-w9/$token/")({
  component: PublicW9UploadPage,
});

const getLinkInfo = async (token: string): Promise<W9PublicLinkInfo> => {
  const response = await w9PublicAxios.get<W9PublicLinkInfoResponse>(
    w9RequestsEndpoints.publicLinkInfo(token),
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error("The W-9 link could not be verified.");
  }
  return response.data.data;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UPLOAD_FAILED_MESSAGE = "The upload failed. Please try again.";

// A provider on this page has no session and no support context, so a dead link has to
// say so instead of inviting an endless retry. Only the API envelope's own message is
// safe to show: getApiErrorMessage otherwise falls through to the axios transport string
// ("Request failed with status code 500") and to S3's XML body on the presigned PUT.
const getUploadErrorMessage = (error: unknown): string => {
  if (!isAxiosError<ApiErrorResponse>(error) || !error.response?.data?.error?.message) {
    return UPLOAD_FAILED_MESSAGE;
  }
  return getApiErrorMessage(error, UPLOAD_FAILED_MESSAGE);
};

function PublicW9UploadPage() {
  const { token } = Route.useParams();
  const [email, setEmail] = useState("");
  const [files, setFiles] = useState<FileDescriptor[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  const {
    data: info,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["w9PublicLink", token],
    queryFn: () => getLinkInfo(token),
    retry: 1,
  });

  const emailValid = EMAIL_RE.test(email.trim());
  const selectedFile = files.find((descriptor) => descriptor.file && !descriptor.error);
  const canUpload = emailValid && Boolean(selectedFile) && !isUploading;

  const handleUpload = async () => {
    if (!selectedFile?.file) return;
    setUploadError(null);
    setIsUploading(true);
    try {
      const presign = await w9PublicAxios.post<W9PublicUploadUrlResponse>(
        w9RequestsEndpoints.publicUploadUrl(token),
        {
          fileName: selectedFile.name,
          contentType: selectedFile.file.type || "application/octet-stream",
          fileSize: selectedFile.file.size,
        },
      );
      if (!presign.data?.success || !presign.data.data?.url || !presign.data.data?.s3Key) {
        throw new Error(UPLOAD_FAILED_MESSAGE);
      }
      const { url, s3Key } = presign.data.data;
      // Presigned S3 PUT: absolute URL, so it must bypass the API baseURL client.
      await axios.put(url, selectedFile.file, {
        headers: { "Content-Type": selectedFile.file.type || "application/octet-stream" },
      });
      await w9PublicAxios.post(w9RequestsEndpoints.publicUploadComplete(token), {
        s3Key,
        fileName: selectedFile.name,
        email: email.trim(),
      });
      setIsDone(true);
    } catch (error) {
      setUploadError(getUploadErrorMessage(error));
    } finally {
      setIsUploading(false);
    }
  };

  const expired = info?.expiresAt ? new Date(info.expiresAt).getTime() < Date.now() : false;

  return (
    <Center mih="100vh" bg="var(--background-secondary, #f4f6f8)" p="var(--spacing-medium)">
      <Paper shadow="md" radius="lg" p="xl" w="min(480px, 100%)">
        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : isError || !info ? (
          <Stack gap="var(--spacing-small)">
            <Title order={3}>We couldn’t verify this link</Title>
            <Text size="sm" c="dimmed">
              Please check the link and try again, or contact the sender for a new one.
            </Text>
          </Stack>
        ) : info.uploaded || isDone ? (
          <Stack align="center" gap="var(--spacing-small)">
            <IconCircleCheckFilled size={48} color="var(--icon-success-default, #12A150)" />
            <Title order={3}>Thank you — we’ve got it</Title>
            <Text size="sm" c="dimmed" ta="center">
              Your W-9 has been received
              {info.serviceProviderFullName ? `, ${info.serviceProviderFullName}` : ""}. You can
              safely close this page.
            </Text>
          </Stack>
        ) : expired ? (
          <Stack gap="var(--spacing-small)">
            <Title order={3}>This link has expired</Title>
            <Text size="sm" c="dimmed">
              Please contact {info.entityName ?? "the sender"} to request a new link.
            </Text>
          </Stack>
        ) : (
          <Stack gap="var(--spacing-small)">
            <Title order={3}>Hi {info.serviceProviderFullName || "there"},</Title>
            <Text size="sm" c="dimmed">
              {info.entityName ?? "We"} need a current copy of your W-9 to keep processing payments.
            </Text>
            {info.expiresAt && (
              <Text size="xs" c="dimmed">
                This link expires on {new Date(info.expiresAt).toLocaleString()}.
              </Text>
            )}
            <TextInput
              label="Your email address"
              required
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={isUploading}
            />
            <FileUploader
              label="Your W-9"
              description="PDF or photo of the signed form — make sure all four corners are visible."
              accept="image/*,application/pdf"
              helpText="PDF or image (max. 25MB)"
              maxSizeMB={25}
              maxFiles={1}
              value={files}
              onChange={setFiles}
            />
            {uploadError && <Alert color="red">{uploadError}</Alert>}
            <Button
              variant="primary"
              iconLeft={<IconUpload size={16} />}
              onClick={handleUpload}
              loading={isUploading}
              isDisabled={!canUpload}
            >
              Submit W-9
            </Button>
            {!emailValid && email.length > 0 && (
              <Text size="xs" c="red">
                Enter a valid email address to enable the upload.
              </Text>
            )}
          </Stack>
        )}
      </Paper>
    </Center>
  );
}
